import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Flag, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

// Reusable "Suggest a correction" affordance for any data record or list.
// entityType: institution | memorial | group | alumni | list
export function SuggestCorrection({
  entityType = "institution", entityId = "", entityName = "", entityUrl = "",
  variant = "link", className = "", label = "Suggest a correction",
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ field: "", issue: "", submitter_name: "", submitter_email: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.issue.trim()) { toast.error("Please describe what needs correcting"); return; }
    setBusy(true);
    try {
      await api.post("/public/corrections", {
        entity_type: entityType, entity_id: entityId, entity_name: entityName,
        entity_url: entityUrl || (typeof window !== "undefined" ? window.location.pathname : ""),
        field: form.field.trim(), issue: form.issue.trim(),
        submitter_name: form.submitter_name.trim() || user?.name || "",
        submitter_email: form.submitter_email.trim() || user?.email || "",
      });
      toast.success("Thanks — our team will review your correction.");
      setOpen(false);
      setForm({ field: "", issue: "", submitter_name: "", submitter_email: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {variant === "button" ? (
          <Button variant="outline" size="sm" className={`rounded-full gap-1.5 ${className}`} data-testid="suggest-correction-open">
            <Flag size={14} /> {label}
          </Button>
        ) : (
          <button type="button" className={`inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-primary ${className}`} data-testid="suggest-correction-open">
            <Flag size={12} /> {label}
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="suggest-correction-dialog">
        <DialogHeader>
          <DialogTitle>Suggest a correction</DialogTitle>
          <DialogDescription>
            {entityName ? <>Spotted something wrong about <strong>{entityName}</strong>? </> : "Spotted something wrong on this page? "}
            Tell us and our team will review it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="corr-field" className="text-xs">What field is wrong? <span className="text-muted-foreground">(optional)</span></Label>
            <Input id="corr-field" value={form.field} onChange={(e) => setForm({ ...form, field: e.target.value })}
              placeholder="e.g. Name, Location, Founded year, Website" className="mt-1" data-testid="correction-field" />
          </div>
          <div>
            <Label htmlFor="corr-issue" className="text-xs">What's the correct information? *</Label>
            <Textarea id="corr-issue" value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })}
              placeholder="Describe what's wrong and what it should be…" rows={3} className="mt-1" data-testid="correction-issue" />
          </div>
          {!user && (
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.submitter_name} onChange={(e) => setForm({ ...form, submitter_name: e.target.value })} placeholder="Your name (optional)" data-testid="correction-name" />
              <Input type="email" value={form.submitter_email} onChange={(e) => setForm({ ...form, submitter_email: e.target.value })} placeholder="Email (optional)" data-testid="correction-email" />
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full gap-1.5" data-testid="correction-submit">
              <PaperPlaneTilt size={14} /> {busy ? "Sending…" : "Submit correction"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default SuggestCorrection;
